import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ROUTES } from "@/app/routes";
import {
  useGetApiConnectorInstallsId,
  usePostApiConnectorInstalls,
} from "@/lib/api/generated/channels/channels";
import { postApiProvidersDescriptors } from "@/lib/api/generated/providers/providers";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import {
  createProviderInstallFieldValues,
  getInstallableProviderDescriptors,
  parseProviderInstallFieldValues
} from "./provider-descriptor-helpers";

const editConnectorSchema = z.object({
  descriptorKey: z.string().min(1),
  status: z.enum(["active", "paused", "error"]),
  fieldValues: z.record(z.string()).default({}),
});

type EditConnectorForm = z.infer<typeof editConnectorSchema>;

export function EditConnectorPage() {
  const { connectorId } = useParams<{ connectorId: string }>();
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: install, isLoading: installLoading } = useGetApiConnectorInstallsId(
    connectorId ?? "",
    { query: { enabled: Boolean(tenantId && connectorId) } },
  );
  const { data: providerDescriptors = [] } = useQuery({
    queryKey: ["providers-descriptors", tenantId],
    queryFn: () => postApiProvidersDescriptors({ tenantId }),
    enabled: Boolean(tenantId),
  });
  const mutation = usePostApiConnectorInstalls();
  const installableProviders = useMemo(
    () => getInstallableProviderDescriptors(providerDescriptors),
    [providerDescriptors],
  );
  const selectedDescriptor = installableProviders.find(
    (record) => record.connectorKey === install?.connectorKey,
  );

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<EditConnectorForm>({
    resolver: zodResolver(editConnectorSchema),
    defaultValues: {
      descriptorKey: "",
      status: "active",
      fieldValues: {},
    },
  });

  useEffect(() => {
    if (!install || !selectedDescriptor) {
      return;
    }
    reset({
      descriptorKey: selectedDescriptor.descriptorKey,
      status: install.status as EditConnectorForm["status"],
      fieldValues: createProviderInstallFieldValues(selectedDescriptor, install),
    });
  }, [install, selectedDescriptor, reset]);

  if (installLoading) {
    return (
      <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
        Loading...
      </p>
    );
  }

  if (!install) {
    return (
      <p className="text-sm" style={{ color: "var(--danger)" }}>
        Backend install not found.
      </p>
    );
  }

  if (!selectedDescriptor) {
    return (
      <p className="text-sm" style={{ color: "var(--danger)" }}>
        No provider descriptor is available for backend type "{install.connectorKey}".
      </p>
    );
  }

  const onSubmit = async (data: EditConnectorForm) => {
    const parsed = parseProviderInstallFieldValues(selectedDescriptor, data.fieldValues, {
      tenantId: tenantId!,
      existingInstall: install,
    });
    if (!parsed.ok) {
      setError(`fieldValues.${parsed.fieldKey}` as const, {
        type: "manual",
        message: parsed.message,
      });
      return;
    }
    clearErrors("fieldValues");

    try {
      await mutation.mutateAsync({
        data: {
          id: connectorId!,
          tenantId: tenantId!,
          connectorKey: selectedDescriptor.connectorKey!,
          status: data.status,
          credentialsRef: parsed.credentialsRef,
          config: parsed.config,
        },
      });
      toast.success(`Backend "${selectedDescriptor.displayName}" updated`);
      navigate(ROUTES.tenantConnectorInstalls(tenantId));
    } catch {
      toast.error("Failed to update backend");
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Edit Backend
      </h1>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="Backend type">
              <input value={selectedDescriptor.displayName} className="form-input" disabled />
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                {selectedDescriptor.connectorKey} via {selectedDescriptor.providerKey}
              </p>
            </FormField>

            <FormField label="Status">
              <select {...register("status")} className="form-input">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="error">Error</option>
              </select>
            </FormField>

            {(selectedDescriptor.install?.fields ?? []).map((field) => (
              <FormField
                key={field.key}
                label={field.label}
                error={errors.fieldValues?.[field.key]?.message}
              >
                {field.type === "json" ? (
                  <textarea
                    {...register(`fieldValues.${field.key}` as const)}
                    rows={4}
                    className="form-input font-mono text-xs"
                    placeholder={field.placeholder ?? "{}"}
                  />
                ) : (
                  <input
                    {...register(`fieldValues.${field.key}` as const)}
                    className="form-input font-mono"
                    placeholder={field.placeholder}
                  />
                )}
                {field.description ? (
                  <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                    {field.description}
                  </p>
                ) : null}
              </FormField>
            ))}

            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              This form is rendered from the provider descriptor for{" "}
              <span className="font-mono">{selectedDescriptor.providerKey}</span>.
            </p>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(ROUTES.tenantConnectorInstalls(tenantId))}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
